import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import TaskCalendar from './components/TaskCalendar';
import TaskQueue from './components/TaskQueue';
import AssignmentPanel from './components/AssignmentPanel';
import ProgressMetrics from './components/ProgressMetrics';
import CommunicationPanel from './components/CommunicationPanel';

const ComplianceTaskManager = () => {
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState('calendar');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const progressMetrics = {
    totalTasks: 89,
    completed: 67,
    inProgress: 15,
    overdue: 7,
    completionRate: 75.3,
    avgResolutionTime: 4.2,
    upcomingThisWeek: 12,
    upcomingThisMonth: 23
  };

  const complianceTasks = [
    {
      id: 1,
      title: 'Waste Disposal Permit Renewal',
      description: 'Submit renewal application for hazardous waste disposal permit',
      regulationType: 'Environmental',
      regulation: 'EPA 40 CFR 262',
      priority: 'high',
      status: 'overdue',
      dueDate: '2025-12-01',
      assignedTo: 'John Smith',
      location: 'Plant 2',
      documents: ['Current Permit', 'Application Form'],
      estimatedHours: 8
    },
    {
      id: 2,
      title: 'Annual Fire Safety Inspection',
      description: 'Schedule and coordinate annual fire safety inspection with local fire marshal',
      regulationType: 'Safety',
      regulation: 'OSHA 1910.157',
      priority: 'high',
      status: 'in-progress',
      dueDate: '2025-12-13',
      assignedTo: 'Sarah Johnson',
      location: 'Plant 1',
      documents: ['Inspection Checklist', 'Previous Report'],
      estimatedHours: 4
    },
    {
      id: 3,
      title: 'Update Safety Training Records',
      description: 'Complete and upload safety training records for new hires',
      regulationType: 'Safety',
      regulation: 'OSHA 1910.120',
      priority: 'medium',
      status: 'pending',
      dueDate: '2025-12-20',
      assignedTo: 'Michael Brown',
      location: 'Plant 3',
      documents: ['Training Certificates', 'Attendance Sheets'],
      estimatedHours: 6
    },
    {
      id: 4,
      title: 'Equipment Calibration Verification',
      description: 'Verify and document calibration for all measurement equipment',
      regulationType: 'Operational',
      regulation: 'ISO 17025',
      priority: 'medium',
      status: 'pending',
      dueDate: '2025-12-27',
      assignedTo: 'Emily Davis',
      location: 'Plant 1',
      documents: ['Calibration Certificates', 'Equipment List'],
      estimatedHours: 12
    },
    {
      id: 5,
      title: 'Labor Law Poster Update',
      description: 'Replace outdated labor law posters with current year versions',
      regulationType: 'Labor',
      regulation: 'FLSA Compliance',
      priority: 'low',
      status: 'pending',
      dueDate: '2025-12-31',
      assignedTo: 'David Wilson',
      location: 'All Locations',
      documents: ['New Posters', 'Compliance Guide'],
      estimatedHours: 2
    }
  ];

  const viewOptions = [
    { value: 'calendar', label: 'Calendar View', icon: 'Calendar' },
    { value: 'list', label: 'List View', icon: 'List' },
    { value: 'board', label: 'Board View', icon: 'Trello' }
  ];

  const priorityFilters = [
    { value: 'all', label: 'All Priorities' },
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const statusFilters = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'overdue', label: 'Overdue' },
    { value: 'completed', label: 'Completed' }
  ];

  const handleBulkAction = (action) => {
    console.log('Bulk action:', action);
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Compliance Task Manager</h1>
            <p className="text-muted-foreground">
              Comprehensive workflow management for statutory compliance requirements
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/statutory-compliance-analytics-dashboard')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="BarChart3" size={20} />
              <span className="text-sm font-medium">View Analytics</span>
            </button>
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
              <Icon name="Plus" size={20} />
              <span className="text-sm font-medium">New Task</span>
            </button>
          </div>
        </div>

        {/* View and Filter Controls */}
        <div className="flex flex-wrap items-center gap-4 bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 border-r border-border pr-4">
            {viewOptions?.map((option) => (
              <button
                key={option?.value}
                onClick={() => setSelectedView(option?.value)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                  selectedView === option?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                <Icon name={option?.icon} size={16} />
                <span className="text-sm font-medium">{option?.label}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Icon name="Filter" size={18} color="var(--color-muted-foreground)" />
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {priorityFilters?.map((filter) => (
                <option key={filter?.value} value={filter?.value}>
                  {filter?.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e?.target?.value)}
              className="px-3 py-2 bg-background border border-border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {statusFilters?.map((filter) => (
                <option key={filter?.value} value={filter?.value}>
                  {filter?.label}
                </option>
              ))}
            </select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => handleBulkAction('reassign')}
              className="inline-flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg hover:bg-muted transition-colors text-sm"
            >
              <Icon name="Users" size={16} />
              Bulk Assign
            </button>
            <button
              onClick={() => handleBulkAction('reminder')}
              className="inline-flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg hover:bg-muted transition-colors text-sm"
            >
              <Icon name="Bell" size={16} />
              Send Reminders
            </button>
          </div>
        </div>
      </div>

      {/* Progress Metrics */}
      <ProgressMetrics metrics={progressMetrics} />

      {/* Main Content Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Task Display - Takes up 2 columns */}
        <div className="lg:col-span-2">
          {selectedView === 'calendar' ? (
            <TaskCalendar tasks={complianceTasks} />
          ) : (
            <TaskQueue tasks={complianceTasks} />
          )}
        </div>

        {/* Assignment and Communication Panel - Takes up 1 column */}
        <div className="space-y-6">
          <AssignmentPanel />
          <CommunicationPanel />
        </div>
      </div>

      {/* Integration Banner */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Link" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Document Repository Integration
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Link compliance tasks directly to supporting documents and evidence in the repository for seamless workflow management.
            </p>
            <button
              onClick={() => navigate('/statutory-compliance-documents')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <Icon name="FileText" size={16} />
              Access Document Repository
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceTaskManager;